import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { Announcement, PortalClass } from '../../types/portal';
import { Plus, Edit2, Trash2, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 10;

const cardClass =
  'bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)]';
const inputClass =
  'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white';
const primaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm';
const secondaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors duration-200 font-medium text-sm';
const dangerBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 font-medium text-sm';

const emptyForm = () => ({
  class_id: '' as string | null,
  title: '',
  content: '',
  is_important: false,
});

const AnnouncementsManager: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [opError, setOpError] = useState('');
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(announcements.length / PAGE_SIZE));
  const pageItems = announcements.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Falls die aktuelle Seite durch Löschen leer wird, zurückspringen
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    const [{ data: aData }, { data: cData }] = await Promise.all([
      supabase.from('announcements').select('*').order('created_at', { ascending: false }),
      supabase.from('portal_classes').select('*').eq('is_active', true).order('title'),
    ]);
    setAnnouncements((aData ?? []) as Announcement[]);
    setClasses((cData ?? []) as PortalClass[]);
    setLoading(false);
  };

  const classMap = Object.fromEntries(classes.map(c => [c.id, c.title]));

  const openCreate = () => { setEditId(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (a: Announcement) => {
    setEditId(a.id);
    setForm({ class_id: a.class_id, title: a.title, content: a.content, is_important: a.is_important });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const payload = {
        class_id: form.class_id || null,
        title: form.title.trim(),
        content: form.content.trim(),
        is_important: form.is_important,
        updated_at: new Date().toISOString(),
      };
      if (editId) {
        const { error } = await supabase.from('announcements').update(payload).eq('id', editId);
        if (error) throw error;
        setAnnouncements(prev => prev.map(a => (a.id === editId ? { ...a, ...payload } : a)));
      } else {
        const { data, error } = await supabase.from('announcements').insert(payload).select().single();
        if (error) throw error;
        setAnnouncements(prev => [data as Announcement, ...prev]);
      }
      setShowForm(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Speichern. Bitte erneut versuchen.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('announcements').delete().eq('id', id);
    if (!error) setAnnouncements(prev => prev.filter(a => a.id !== id));
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-1">Portal</p>
          <h2 className="text-3xl font-bold text-[#1f1f1f]">Ankündigungen</h2>
        </div>
      {opError && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{opError}</p>
          <button onClick={() => setOpError('')} className="text-red-400 hover:text-red-600 text-xs font-medium shrink-0">Schließen</button>
        </div>
      )}
        <button onClick={openCreate} className={primaryBtn}>
          <Plus className="h-4 w-4 mr-1" />Erstellen
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#eadfd6]">
              <h3 className="text-lg font-bold text-[#1f1f1f]">{editId ? 'Ankündigung bearbeiten' : 'Neue Ankündigung'}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-xl hover:bg-[#f8f3ef]">
                <X className="h-5 w-5 text-[#6b6b6b]" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  Zielgruppe
                </label>
                <select
                  value={form.class_id ?? ''}
                  onChange={e => setForm(p => ({ ...p, class_id: e.target.value || null }))}
                  className={inputClass}
                >
                  <option value="">Alle Schüler</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Titel *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className={inputClass}
                  required
                  placeholder="Titel der Ankündigung"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Inhalt *</label>
                <textarea
                  value={form.content}
                  onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                  className={inputClass + ' resize-none'}
                  rows={5}
                  required
                  placeholder="Text der Ankündigung…"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_important}
                  onChange={e => setForm(p => ({ ...p, is_important: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm font-medium">Als wichtig markieren</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className={primaryBtn}>
                  <Check className="h-4 w-4 mr-1" />{saving ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className={secondaryBtn}>Abbrechen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] p-6 w-full max-w-sm">
            <p className="font-semibold text-[#1f1f1f] mb-4">Ankündigung wirklich löschen?</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDelete)} className={dangerBtn}>Löschen</button>
              <button onClick={() => setConfirmDelete(null)} className={secondaryBtn}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading ? (
          [1, 2, 3].map(i => <div key={i} className="animate-pulse bg-white rounded-[24px] border border-[#eadfd6] h-24" />)
        ) : announcements.length === 0 ? (
          <div className={cardClass + ' p-10 text-center text-[#6b6b6b]'}>Keine Ankündigungen vorhanden.</div>
        ) : (
          pageItems.map(a => (
            <div
              key={a.id}
              className={`${cardClass} p-5 border-l-4 ${a.is_important ? 'border-l-red-500' : 'border-l-[#970000]/20'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-bold text-[#1f1f1f]">{a.title}</h3>
                    {a.is_important && (
                      <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Wichtig</span>
                    )}
                  </div>
                  <p className="text-sm text-[#6b6b6b] line-clamp-2 whitespace-pre-wrap">{a.content}</p>
                  <div className="flex gap-2 mt-2">
                    {a.class_id ? (
                      <span className="text-xs bg-[#f8f3ef] text-[#5f5148] px-2 py-0.5 rounded-full">{classMap[a.class_id] ?? 'Klasse'}</span>
                    ) : (
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">Alle Schüler</span>
                    )}
                    <span className="text-xs text-[#9b9490]">
                      {new Date(a.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(a)} className="p-2 rounded-xl text-[#6b6b6b] hover:bg-[#f8f3ef]">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => setConfirmDelete(a.id)} className="p-2 rounded-xl text-red-500 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-9 h-9 rounded-full border border-[#d9c9bd] flex items-center justify-center text-[#5f5148] hover:bg-[#f8f3ef] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Vorherige Seite"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`min-w-[32px] h-8 px-2 rounded-full text-sm font-semibold transition-colors ${
                  page === i + 1
                    ? 'bg-[#970000] text-white'
                    : 'text-[#5f5148] hover:bg-[#f8f3ef]'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-9 h-9 rounded-full border border-[#d9c9bd] flex items-center justify-center text-[#5f5148] hover:bg-[#f8f3ef] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Nächste Seite"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {!loading && announcements.length > 0 && (
        <p className="text-center text-xs text-[#9b9490]">
          {announcements.length} Ankündigungen gesamt · Seite {page} von {totalPages}
        </p>
      )}
    </div>
  );
};

export default AnnouncementsManager;
